/**
 * A backend hívása. Egyetlen hely, ahol `fetch` szerepel.
 *
 * A kérés-kontextus SZERVEROLDALI — a felület nem küld bérlőt és nem küld
 * cég-szűrőt. A munkamenetet a böngésző viszi, `httpOnly` sütiben (#14); hogy
 * mit lát, azt a szerver dönti el a saját kikényszerítési pontján (2.6/23).
 * Ha a szűrő a felületről jönne, a szűkítés a kliensen múlna, és az nem
 * szűkítés.
 */

import { env } from '@/config/env';

/**
 * A CSRF-token sütijének neve és a fejléc, amiben visszaküldjük. A szerver
 * `session-cookie.ts`-e ugyanezt a két nevet használja — ha elválnak, MINDEN
 * írás 403-mal jön vissza.
 */
const CSRF_COOKIE = 'fakt.csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/** Amihez nem kell CSRF-token — ugyanaz a halmaz, mint a szerver őrében. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A CSRF-TOKEN KIOLVASÁSA A SÜTIBŐL (#14).
 *
 * A munkamenet `httpOnly` sütiben utazik — azt a JS NEM éri el, és nem is
 * kell neki. A CSRF-token sütije viszont SZÁNDÉKOSAN olvasható: a szerver
 * ebből várja vissza a `X-CSRF-Token` fejlécet, és a kettő egyezéséből
 * tudja, hogy a kérést a MI lapunk indította.
 *
 * ⚠️ EZ NEM AZ AUTENTIKÁCIÓ. Ha valaki ezt a tokent ellopja, semmire nem
 * megy vele: a munkamenetet a másik, olvashatatlan süti hordozza.
 */
function csrfToken(): string | null {
  // A `document.cookie` egyetlen sztring, `a=1; b=2` alakban. Az érték
  // tartalmazhat `=`-t (base64url nem, de a HMAC-elválasztó pont igen),
  // ezért CSAK az első egyenlőségjelnél vágunk.
  for (const part of document.cookie.split('; ')) {
    const at = part.indexOf('=');
    if (at > 0 && part.slice(0, at) === CSRF_COOKIE) {
      return decodeURIComponent(part.slice(at + 1));
    }
  }
  return null;
}

/**
 * A kérés kísérő fejlécei.
 *
 * ⚠️ A `credentials: 'include'` NEM ELHAGYHATÓ, még azonos originen sem —
 * a `fetch` alapértelmezése `same-origin`, ami itt ugyan elég lenne, de a
 * `VITE_API_URL`-lel másik originre állítva NÉMÁN elhagyná a sütit, és
 * minden kérés 401 lenne. Kiírva a szándék egyértelmű.
 */
export function withSession(init: RequestInit): RequestInit {
  // ⚠️ A CSRF-TOKEN CSAK AZ ÍRÁSOKHOZ KELL, és csak azokhoz olvassuk ki.
  // A szerver őre ugyanezt a határt húzza (`csrf.guard.ts`): a GET/HEAD/
  // OPTIONS definíció szerint nem változtat semmit. Olvasásnál a sütihez
  // hozzányúlni nemcsak fölösleges, hanem ÁRT is — a `document.cookie`
  // getter valódi `URL` konstruktort kíván, és egy olyan hívási helyen,
  // ahol azt kicserélték (objektum-URL-ek), az egész kérés elszállna.
  const method = (init.method ?? 'GET').toUpperCase();
  const token = SAFE_METHODS.has(method) ? null : csrfToken();

  return {
    ...init,
    credentials: 'include',
    headers: {
      ...(token === null ? {} : { [CSRF_HEADER]: token }),
      ...init.headers,
    },
  };
}

// A házirend szerint közvetlen `import.meta.env` nincs — a config/env dob,
// ha egy kötelező változó hiányzik.
const BASE_URL = env.apiUrl;

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(
    `${BASE_URL}${path}`,
    withSession({ ...init, headers: { 'Content-Type': 'application/json', ...init.headers } })
  );

  return unwrap<T>(response);
}

/**
 * Fájlfeltöltés — multipart, ugyanazzal a hibakezeléssel.
 *
 * ⚠️ NINCS `Content-Type` FEJLÉC, ÉS EZ NEM FELEDÉKENYSÉG. Multipartnál a
 * fejléc a HATÁROLÓT is tartalmazza (`boundary=…`), amit csak a böngésző
 * ismer — kézzel ráírva a szerver egy üres törzset lát, hibaüzenet nélkül.
 * Ezért nem elég az `api()`-t egy `FormData` törzzsel meghívni.
 *
 * A mezőnév (`file`) a szerver `FilesInterceptor('file', …)`-ével EGYEZIK. Ha
 * elválik, a kérés 400-zal jön vissza „nincs fájl"-lal — ami igaz is, csak
 * nem segít.
 *
 * ⚠️ A MEZŐNÉV TÖBBES SZÁMBAN IS `file`, ÉS EZ A SZERVER DÖNTÉSE. A multipart
 * ugyanazt a mezőnevet többször is hordozhatja, ezért a köteg egyszerűen
 * többször teszi bele — nincs `files` mezőnév, amire át kellene állni.
 *
 * A KÖTEG MÉRETÉT NEM ITT KORLÁTOZZUK. Ez a függvény azt küldi el, amit kap;
 * a szerver kérésenkénti korlátaihoz a hívó tervezi meg a részleteket
 * (`features/uploads/chunking.ts`).
 */
export async function upload<T>(
  path: string,
  files: readonly File[],
  signal?: AbortSignal,
  /**
   * `single-document` = A FÁJLOK EGY BIZONYLAT OLDALAI, ebben a sorrendben
   * (`szkenneles-terv.md` Sz2). Hiányában a szerver a mai, fájlonként egy
   * bizonylat viselkedést adja — ezért additív a változás mindkét oldalon.
   */
  grouping?: 'single-document'
): Promise<T> {
  const body = new FormData();
  for (const file of files) {
    body.append('file', file);
  }
  if (grouping) {
    // ⚠️ A MEZŐ A FÁJLOK UTÁN MEGY. A multer a mezőket ABBAN A SORRENDBEN
    // dolgozza fel, ahogy a multipart-folyamban állnak; a fájlok előtti
    // szöveges mező egyes feldolgozóknál a `body`-ba kerül ugyan, de a
    // sorrendre építeni fölösleges kockázat. A NestJS `@Body()`-ja mindkét
    // esetben megkapja.
    body.append('grouping', grouping);
  }

  const response = await fetch(
    `${BASE_URL}${path}`,
    withSession({
      method: 'POST',
      body,
      // ⚠️ A `fetch`-NEK NINCS ALAPÉRTELMEZETT IDŐKORLÁTJA. Egy megakadt
      // mobilkapcsolaton a promise soha nem dől el: a felület `busy`-ban ragad,
      // mindkét belépési pont letiltva, és nincs mit tenni. A jel a hívóé —
      // ő tudja, mekkora adagot küld, és ő tud „Mégsem"-et kínálni.
      signal,
    })
  );

  return unwrap<T>(response);
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // A szerver üzenete magyarul jön; a felület azt mutatja, nem egy sajátot
    // — különben két helyen kellene ugyanazt karbantartani.
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, body?.message ?? '');
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Amit a `/auth/me` ad vissza — a kontextus felületi tükre. */
export interface Me {
  actor:
    | {
        kind: 'user' | 'support';
        userId?: string;
        role?: 'ADMIN' | 'ACCOUNTANT' | 'CLIENT';
        name: string;
        locale: string;
        companyId?: string | null;
      }
    /**
     * MASZK (#76) — egy irodai felhasználó a cég NEVÉBEN. A `name` a KÖNYVELŐ
     * neve, a `companyName` azé, akinek a nevében dolgozik; a felület mindkettőt
     * kiírja, hogy a kettő soha ne keveredjen össze.
     */
    | {
        kind: 'impersonation';
        userId: string;
        grantId: string;
        companyId: string;
        companyName: string;
        /** ISO-időbélyeg — a fix lejárat, nem hosszabbodik. */
        expiresAt: string;
        name: string;
        locale: string;
      };
  companies:
    | { kind: 'tenant' }
    | { kind: 'assigned'; companyIds: string[] }
    | { kind: 'single'; companyId: string };
}

export interface LoginResponse {
  user: { id: string; name: string; email: string; role: string; locale: string };
}

/**
 * Amit a varázslink megnyitása mond — `POST /auth/magic-link/check`.
 *
 * A `valid: false` NÉGY OKOT fed le egyszerre (lejárt, elhasznált, ismeretlen,
 * vagy már van jelszó), és ez a szerver döntése: a megkülönböztetés csak
 * annak mondana valamit, aki a tokent találgatja. A felületnek mind a négy
 * ugyanaz a mondat — `auth.linkExpired`.
 */
export interface MagicLinkCheck {
  valid: boolean;
  name: string | null;
}
